// ============================================================
// src/components/auth/ProtectedRoute.tsx
// Protège les pages qui nécessitent une connexion
// ============================================================

import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { useTranslation } from "react-i18next";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireVerified?: boolean; // Exiger un profil Airbnb vérifié
  requireSubscription?: boolean; // Exiger un abonnement actif
}

export default function ProtectedRoute({
  children,
  requireVerified = false,
  requireSubscription = false,
}: ProtectedRouteProps) {
  const { t } = useTranslation(["common"]);
  const { user, profile, loading } = useAuth();
  const location = useLocation();

  // Pendant le chargement, afficher un spinner
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-terra-200 border-t-terra-500 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">{t("common:migrated.chargement___")}</p>
        </div>
      </div>
    );
  }

  // Pas connecté → rediriger vers login
  if (!user) {
    const nextPath = `${location.pathname}${location.search}${location.hash}`;
    const params = new URLSearchParams();

    if (nextPath.startsWith("/") && !nextPath.startsWith("//")) {
      params.set("next", nextPath);
    }

    return (
      <Navigate
        to={`/auth?${params.toString()}`}
        state={{ from: location }}
        replace
      />
    );
  }

  // Profil Airbnb non vérifié (si requis)
  if (requireVerified && profile && !profile.airbnb_verified) {
    return <Navigate to="/members-map" replace />;
  }

  // Pas d'abonnement actif (si requis)
  if (requireSubscription && profile) {
    const hasActiveSubscription =
      profile.subscription_plan !== "free" &&
      profile.subscription_expires_at &&
      new Date(profile.subscription_expires_at) > new Date();

    if (
      !hasActiveSubscription &&
      !profile.is_founding_member &&
      profile.role !== "admin"
    ) {
      return <Navigate to="/payment" replace />;
    }
  }

  return <>{children}</>;
}
